import { Link } from "react-router-dom";
import {
  ShieldCheck, RefreshCcw, Code2, BadgeCheck, Archive, DatabaseBackup,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import KineticHero from "../components/KineticHero";
import PartnerMarquee from "../components/PartnerMarquee";
import CapabilityFlow from "../components/CapabilityFlow";
import CtaBand from "../components/CtaBand";
import GlowCard from "../components/GlowCard";
import BrandMark from "../components/BrandMark";
import DeliveryChain from "../components/DeliveryChain";
import { Reveal, SectionHeading, Eyebrow } from "../components/Reveal";

const serviceIcons = [ShieldCheck, RefreshCcw, Code2, BadgeCheck, Archive, DatabaseBackup];

export default function Home() {
  const { t } = useLang();
  const lv = t.home.levels;
  const pb = t.home.partnersBand;
  const so = t.home.sovereign;
  const sv = t.home.services;

  return (
    <>
      <KineticHero />
      <PartnerMarquee />

      {/* Three levels */}
      <section className="py-24 lg:py-32" data-testid="levels-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={lv.eyebrow} title={lv.title} />
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-400 max-w-2xl">{lv.text}</p>
          </Reveal>
          <div className="mt-8">
            <DeliveryChain chain={lv.chain} />
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {lv.items.map((it, i) => (
              <Reveal key={it.k} delay={i * 0.1} className="h-full">
                <div
                  className="relative glass rounded-lg p-8 h-full group hover:border-sovereign/40 transition-colors duration-500"
                  data-testid={`level-card-${i}`}
                >
                  <span className="font-mono text-5xl font-bold text-sovereign/15 group-hover:text-sovereign/35 transition-colors duration-500">
                    {it.k}
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{it.name}</h3>
                  <p className="mt-3 font-mono text-[11px] tracking-[0.16em] uppercase text-slate-400 leading-loose">{it.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Partners band */}      <section className="relative py-24 lg:py-32 border-t border-sovereign/10 overflow-hidden" data-testid="partners-band">
        <div className="absolute inset-0 hud-grid opacity-[0.04] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={pb.eyebrow} title={pb.title} />
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-400 max-w-2xl">{pb.text}</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {pb.partners.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1} className="h-full">
                <Link to={p.link} data-testid={`home-partner-card-${i}`} className="block h-full group">
                  <div className="glass rounded-lg h-full p-8 relative overflow-hidden transition-all duration-500 group-hover:border-sovereign/45 group-hover:-translate-y-1.5 group-hover:shadow-[0_20px_60px_rgba(0,240,255,0.08)]">
                    <BrandMark name={p.name} />
                    <p className="mt-7 font-display font-bold text-slate-100">{p.label}</p>
                    <p className="mt-3 font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 leading-loose">{p.tags}</p>
                    <span className="mt-8 inline-flex items-center gap-2 text-sovereign font-mono text-[11px] tracking-[0.2em] uppercase">
                      {p.linkText}
                      <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-14">
            <Link
              to="/partners"
              data-testid="partners-band-cta"
              className="inline-flex items-center gap-3 border border-sovereign/40 text-sovereign px-8 py-4 font-mono text-xs tracking-[0.25em] uppercase hover:bg-sovereign/10 hover:shadow-[0_0_30px_rgba(0,240,255,0.2)] transition-all duration-300"
            >
              {pb.cta}
              <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Sovereign AI banner */}
      <section className="relative py-24 lg:py-28 border-y border-konect/20 overflow-hidden" data-testid="sovereign-banner">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 70% at 50% 50%, rgba(255,153,0,0.07), transparent 65%)" }}
          aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <Eyebrow color="text-konect">{so.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-slate-100">
              {so.title}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 text-slate-300 max-w-2xl mx-auto">{so.text}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              {so.tags.map((tag, i) => (
                <span
                  key={i}
                  className="border border-konect/30 bg-konect/5 px-3.5 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-konect/90"
                  data-testid={`sovereign-tag-${i}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.26}>
            <Link
              to="/datacenter-ai"
              data-testid="sovereign-cta"
              className="mt-10 inline-flex items-center gap-3 border border-konect/60 text-konect px-8 py-4 font-mono text-xs tracking-[0.25em] uppercase hover:bg-konect/10 hover:shadow-[0_0_30px_rgba(255,153,0,0.2)] transition-all duration-300"
            >
              {so.cta}
              <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      <CapabilityFlow />

      {/* Services */}
      <section className="py-24 lg:py-32 border-t border-sovereign/10" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={sv.eyebrow} title={sv.title} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sv.items.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <Reveal key={i} delay={(i % 3) * 0.08} className="h-full">
                  <GlowCard className="h-full" testid={`service-card-${i}`}>
                    <Icon className="w-7 h-7 text-sovereign" strokeWidth={1.5} aria-hidden="true" />
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-100">{s.name}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  </GlowCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand title={t.home.cta.title} text={t.home.cta.text} />
    </>
  );
}
